import {
  MapPageListingResponse,
  SortBy,
  MapPageClusterRequest,
  MapPageFilter,
} from '@zoocasa/go-search';
import { MapPageBoundarySearchRequest, MapPageBoundarySearchResponse } from '@zoocasa/go-search';
import { GetAreaPolygonRequest, GetAreaPolygonResponse } from '@zoocasa/go-search';

import { fetchWithRetry } from 'utils/fetchWithRetry';
import { FetchOptions } from 'types/fetchWithRetry';
import { HttpRequestMethodTypes } from 'types';
import { getGoSearchHost, isServerSide } from 'utils/host-config';
import { MAP_PAGE_CONSTANTS } from 'constants/pagination';

export interface AreaPolygonSearchCriteria {
  slug?: string;
  areaSearch?: {
    neighbourhood?: string;
    city?: string;
    province?: string;
    addressType?: string;
  };
  locationSearch?: {
    location?: string;
    province?: string;
    addressType?: string;
  };
  coordinates?: {
    latitude: number;
    longitude: number;
    addressType?: string;
  };
}

export async function fetchClusters(filter: MapPageFilter, sortBy: SortBy = SortBy.DateDesc, signal?: AbortSignal, size: number = MAP_PAGE_CONSTANTS.DEFAULT_CLUSTER_DATA_SIZE, host: string = getGoSearchHost(isServerSide())) {
  const clustersEndpointUrl = `${host}/api/map/clusters?limit=${size}`;
  let resp: MapPageListingResponse;

  try {
    const mapPageClusterRequest = MapPageClusterRequest.fromPartial({ sortBy: sortBy, filter });
    const requestBody = MapPageClusterRequest.encode(mapPageClusterRequest).finish();

    const options: FetchOptions = { method: HttpRequestMethodTypes.POST, body: requestBody, signal };
    const response = await fetchWithRetry(clustersEndpointUrl, options);

    if (response.ok) {
      const content = await response.blob();
      const buffer = await content.arrayBuffer();
      resp = MapPageListingResponse.decode(new Uint8Array(buffer));
    } else {
      console.error(`fetch clusters within boundary: ${response.status} ${response.statusText} ${response.type}`);
      resp = MapPageListingResponse.fromPartial({});
    }
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.debug('fetch clusters within boundary request was aborted');
      throw error;
    }
    console.error('failed to fetch map page clusters: %s', error);
    resp = MapPageListingResponse.fromPartial({});
  }

  return resp;
}

export async function fetchListings(filter: MapPageFilter, sortBy: SortBy = SortBy.DateDesc, page: string = '', signal?: AbortSignal, size: number = MAP_PAGE_CONSTANTS.DEFAULT_CLUSTER_DATA_SIZE, host = getGoSearchHost(isServerSide())) {
  const boundaryEndpointUrl = `${host}/api/map/boundary?limit=${size}${page.length > 0 ? `&page=${page}` : ''}`;
  let resp: MapPageBoundarySearchResponse;

  try {
    const mapPageBoundarySearchRequest = MapPageBoundarySearchRequest.fromPartial({ sortBy: sortBy, filter });
    const requestBody = MapPageClusterRequest.encode(mapPageBoundarySearchRequest).finish();

    const options: FetchOptions = { method: HttpRequestMethodTypes.POST, body: requestBody, signal };
    const response = await fetchWithRetry(boundaryEndpointUrl, options);

    if (response.ok) {
      const content = await response.blob();
      const buffer = await content.arrayBuffer();
      resp = MapPageBoundarySearchResponse.decode(new Uint8Array(buffer));
    } else {
      console.error(`fetch listings within boundary: ${response.status} ${response.statusText} ${response.type}`);
      resp = MapPageBoundarySearchResponse.fromPartial({});
    }
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.debug('fetch listings within boundary request was aborted');
      throw error;
    }
    console.error('failed to fetch listings within boundary: %s', error);
    resp = MapPageBoundarySearchResponse.fromPartial({});
  }
  return resp;
}

/**
 * @typedef {object} AreaPolygonSearchCriteria
 * @property {string} [slug] - Search by slug (e.g., "toronto-on")
 * @property {object} [areaSearch] - Search by geographical area components
 * @property {string} [areaSearch.neighbourhood] - Neighbourhood name
 * @property {string} [areaSearch.city] - City name
 * @property {string} [areaSearch.province] - Province/State code (e.g., "ON")
 * @property {string} [areaSearch.addressType] - Type of address (e.g., "city")
 * @property {object} [coordinates] - Search by latitude/longitude coordinates
 * @property {number} coordinates.latitude - Latitude value
 * @property {number} coordinates.longitude - Longitude value
 * @property {string} [coordinates.addressType] - Type of address (e.g., "city")
 */

/**
 * Fetches area polygons from the API based on different search criteria.
 *
 * This function processes HTTP requests to search for area polygons using one of the following criteria:
 * - A slug (e.g., "toronto-on")
 * - Geographical area components (neighbourhood, city, province, addressType)
 * - Latitude/longitude coordinates
 *
 * It expects requests to be sent in protobuf format (GetAreaPolygonRequest) and returns
 * responses in protobuf format (GetAreaPolygonResponse).
 *
 * @param {AreaPolygonSearchCriteria} searchCriteria - The criteria to use for the search.
 * @param {AbortSignal} [signal] - Optional AbortSignal for cancelling the request.
 * @param {string} [host] - Optional API host override, defaults to the go-search host.
 * @returns {Promise<GetAreaPolygonResponse | null>} The decoded area polygon response, or null if an error occurs.
 */
export async function fetchAreaPolygons(
  searchCriteria: AreaPolygonSearchCriteria,
  signal?: AbortSignal,
  host: string = getGoSearchHost(isServerSide())
) {
  const areaPolygonsEndpointUrl = `${host}/api/area-polygons/search`;
  let resp: GetAreaPolygonResponse;

  try {
    // Create the proper protobuf request based on which search criteria is provided
    let requestObj: any = {};

    if (searchCriteria.slug) {
      requestObj = { slug: searchCriteria.slug };
    } else if (searchCriteria.areaSearch) {
      requestObj = {
        areaSearch: {
          neighbourhood: searchCriteria.areaSearch.neighbourhood || '',
          city: searchCriteria.areaSearch.city || '',
          province: searchCriteria.areaSearch.province || '',
          addressType: searchCriteria.areaSearch.addressType || '',
        },
      };
    } else if (searchCriteria.locationSearch) {
      requestObj = {
        locationSearch: {
          location: searchCriteria.locationSearch.location || '',
          province: searchCriteria.locationSearch.province || '',
          addressType: searchCriteria.locationSearch.addressType || '',
        },
      };
    } else if (searchCriteria.coordinates) {
      requestObj = {
        coordinates: {
          latitude: searchCriteria.coordinates.latitude,
          longitude: searchCriteria.coordinates.longitude,
          addressType: searchCriteria.coordinates.addressType || '',
        },
      };
    } else {
      throw new Error('No valid search criteria provided');
    }

    // Use the proper protobuf encoding
    const request = GetAreaPolygonRequest.fromPartial(requestObj);
    const requestBody = GetAreaPolygonRequest.encode(request).finish();

    const options: FetchOptions = {
      method: HttpRequestMethodTypes.POST,
      body: requestBody,
      signal,
    };

    const response = await fetchWithRetry(areaPolygonsEndpointUrl, options);

    if (response.ok) {
      const content = await response.blob();
      const buffer = await content.arrayBuffer();

      // Use proper protobuf decoding
      resp = GetAreaPolygonResponse.decode(new Uint8Array(buffer));
    } else {
      console.error(`fetch area polygons: ${response.status} ${response.statusText} ${response.type}`);
      resp = GetAreaPolygonResponse.fromPartial({});
    }
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.debug('fetch area polygons request was aborted');
      throw error;
    }
    console.error('failed to fetch area polygons: %s', error);
    resp = GetAreaPolygonResponse.fromPartial({});
  }
  return resp;
}